import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BugReportService } from './bug-report.service';
import { Optional } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SnackBarService } from '../../core/snack-bar.service';
import { ENVIRONMENT_DI } from '../../environment/environment';
import { Environment } from '../../environment/environment.base';

@Component({
  selector: 'app-bugreport',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    TranslateModule,
  ],
  templateUrl: './bugreport.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bugreport.component.scss',
})
export class BugreportComponent implements OnInit {
  bugReportForm!: FormGroup;
  selectedFiles: File[] = [];
  isSubmitting = false;
  isDialog = false;

  severityOptions = [
    { value: 1, label: 'Critical', icon: '🔴' },
    { value: 2, label: 'Major', icon: '🟠' },
    { value: 3, label: 'Minor', icon: '🟡' },
    { value: null, label: 'Reset' },
  ];

  constructor(
    private fb: FormBuilder,
    private bugReportService: BugReportService,
    private snackbarService: SnackBarService,
    @Optional() private dialogRef: MatDialogRef<BugreportComponent>,
    @Inject(ENVIRONMENT_DI) readonly environment: Environment,
  ) {
    // Check if opened as dialog
    this.isDialog = !!this.dialogRef;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.bugReportForm = this.fb.group({
      title: ['', Validators.required],
      description: ['1. \n2. \n3. \n\nExpected: \n\nActual: ', Validators.required],
      screenshots: [null, Validators.required],
      severity: [null, Validators.required],
      prodBug: [this.environment.isProduction],
      reportedByCsm: [!this.environment.isDevelopment],
      affectedCustomer: [false],
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (let i = 0; i < input.files.length; i++) {
        this.selectedFiles.push(input.files[i]);
      }
      this.bugReportForm.patchValue({ screenshots: this.selectedFiles });
      this.bugReportForm.get('screenshots')?.updateValueAndValidity();
      input.value = '';
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.bugReportForm.patchValue({ screenshots: this.selectedFiles });
    this.bugReportForm.get('screenshots')?.updateValueAndValidity();
  }

  resetFileInput(): void {
    // Reset the file input
    const fileInput = document.getElementById('screenshots') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.bugReportForm.patchValue({ screenshots: null });
    this.bugReportForm.get('screenshots')?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.bugReportForm.invalid) {
      return;
    }

    this.isSubmitting = true;

    const formData = new FormData();
    const formValue = this.bugReportForm.value;

    // Add form fields to FormData
    formData.append('title', formValue.title);
    formData.append('description', formValue.description);
    formData.append('prodBug', formValue.prodBug);
    formData.append('reportedByCsm', formValue.reportedByCsm);
    formData.append('affectedCustomer', formValue.affectedCustomer);
    formData.append('severity', formValue.severity);

    // Add files if selected
    if (this.selectedFiles.length > 0) {
      this.selectedFiles.forEach((file, index) => {
        formData.append('screenshots', file, file.name);
      });
    }

    this.bugReportService.submitBugReport(formData).subscribe({
      next: () => {
        this.isSubmitting = false;
        // Only close if it's a dialog
        if (this.isDialog) {
          this.dialogRef.close(true); // Close with success result
        } else {
          // Handle non-dialog success
          this.bugReportForm.reset();
          this.selectedFiles = [];
          this.resetFileInput();
        }
        this.snackbarService.success('Bug report submitted successfully');
      },
      error: () => {
        this.isSubmitting = false;
        this.snackbarService.error('Failed to submit bug report', 'Please try again');
      },
    });
  }

  onPaste(event: ClipboardEvent) {
    if (!event.clipboardData) return;
    const items = event.clipboardData.items;

    for (const item of items as any) {
      const file = item.getAsFile();
      this.addFile(file);
    }
    this.bugReportForm.patchValue({ screenshots: this.selectedFiles });
    this.bugReportForm.get('screenshots')?.updateValueAndValidity();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
    this.bugReportForm.patchValue({ screenshots: this.selectedFiles });
    this.bugReportForm.get('screenshots')?.updateValueAndValidity();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  private addFiles(fileList: FileList | File[]) {
    for (const file of Array.from(fileList)) {
      this.addFile(file);
    }
  }

  private addFile(file: File) {
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          (file as any).previewUrl = reader.result as string;
        };
        reader.readAsDataURL(file);
      }
      this.selectedFiles.push(file);
    }
  }

  closeDialog(): void {
    if (this.isDialog) {
      this.dialogRef.close();
    }
    // If not in dialog mode, maybe navigate away or do nothing
  }
}
